import { trackRegistration } from '../utils/metaTracking';
import { useState } from "react";
import "./Register.css";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../translations.js";
import { saveInteraction } from "../components/Interaction.js";
import { fireTrialConversion } from "../utils/adsTracking.js";
import { signupContext, pushSignupEvent } from "../utils/sourceTracking.js";
import { returnToFromSearch } from "../utils/session";


function Register({ onRegister }) {
  const { t, i18n } = useTranslation();
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [language, setLanguage] = useState(i18n.language || "en");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (password !== confirmPassword) {
      setError(t("passwords_do_not_match"));
      setLoading(false);
      return;
    }

    try {
      saveInteraction({ action: "register", email: email, recaptcha_score: 1 });
      await registerUser();
    } catch (err) {
      console.error("Regsiter error", err);
      setError(t("registration_error"));
      setLoading(false);
    }
  };

  const registerUser = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}register/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Send the ub_attrib attribution cookie along. Same-origin in
        // production (portal.ultimo-bots.com/api) where this changes nothing;
        // needed for the cookie to reach a cross-port local API.
        credentials: "include",
        body: JSON.stringify({
          email,
          password,
          given_name: givenName,
          family_name: familyName,
          language,
          send_email: true,
          // which flow sent the visitor here (ChatGPT/MCP, a Wix app, an invite); only for "where customers come from"
          signup_context: signupContext(),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Google Ads signup conversion: real new registration on the website
        // flow (never fired on login; Wix/WordPress flows use other pages).
        fireTrialConversion();
        trackRegistration(data.user_id, "direct");
        pushSignupEvent("email", signupContext());
        localStorage.setItem("language", language);
        i18n.changeLanguage(language);
        onRegister(true, data.token, data.user_id, data.first_name, language);
      } else {
        const errData = await response.json();
        setError(errData.detail || t("registration_failed"));
      }
    } catch (err) {
      setError(t("registration_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    const returnTo = returnToFromSearch(window.location.search);
    window.location.href = returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : "/login";
  };
  

  return (
    <div className="register-form-container">
      <form className="register-form" onSubmit={handleSubmit}>
        <h1 className="register-form-title">{t("new_account_register")}</h1>

        <div className="register-input">
          <input type="text" value={givenName} onChange={(e) => setGivenName(e.target.value)} placeholder={t("first_name")} required />
        </div>
        <div className="register-input">
          <input type="text" value={familyName} onChange={(e) => setFamilyName(e.target.value)} placeholder={t("last_name")} required />
        </div>
        <div className="register-input">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email")} required />
        </div>
        <div className="register-input">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("password")} required />
        </div>
        <div className="register-input">
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t("confirm_password")} required />
        </div>
        <div className="register-input">
          <select value={language} onChange={(e) => {
            const lang = e.target.value;
            setLanguage(lang);
            localStorage.setItem("language", lang);
            i18n.changeLanguage(lang);
          }}>
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </div>

        <div className="submit-area">
          <button className="submit-register" type="submit" disabled={loading}>
            {t("register")}
            {loading && <span className="register-loader"></span>}
          </button>
        </div>

        {error && <div className="register-error">{error}</div>}

        <div className="signup-row">
          <span>{t("register_have_account_prompt")}</span>
          <span className="signup-link" onClick={handleLoginRedirect}>{t("login")}</span>
        </div>
      </form>
    </div>
  );
}

export default Register;
